import { useCallback, useMemo } from 'react';

import { IMenuActionParams, NewValueParams } from '@mint-lib/ag-grid/community';
import type { GoalCellValue } from '@mint-lib/ag-grid/editors';
import { GROUP_AUTO_COLUMN_ID } from '@mint-lib/ag-grid/enterprise';
import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import { UpdateColumnParams } from '../api/mutations/useUpdateColumn.js';
import { usePlanningGridApi } from '../atoms/gridApi.js';
import { EMPTY_CELL_VALUE } from '../consts/grid.consts.jsx';
import { SYSTEM_CURRENCY } from '../consts/index.js';
import type { CellHandlersType } from '../types/grid.types.js';
import { getColName, updateErrorInContext } from '../utils/grid.utils.js';
import { useSetConfirmationModal } from './useSetConfirmationCurrency.js';

type UseCellEditorHandlersParams = {
  updateColumn: (
    params: UpdateColumnParams,
  ) => Promise<MediaPlan.GetMediaDataResponse>;
};

const getErrorValue = (value: UpdateColumnParams['value']) => {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value.refUrn
    : value;
};

export const useCellEditorHandlers = ({
  updateColumn,
}: UseCellEditorHandlersParams) => {
  const { t } = useTranslation('@myn/planning_v2');
  const ff = useFlags('@mint/arm', ['planningV2currencyWithoutConversion']);

  const [gridApi] = usePlanningGridApi();

  const updateColumnWithErrorHandling = useCallback(
    async (params: UpdateColumnParams) => {
      const colId =
        params.field === 'name' ? GROUP_AUTO_COLUMN_ID : params.field;

      try {
        await updateColumn(params);

        updateErrorInContext({
          gridApi,
          rowId: params.rowId,
          colId,
          errorData: null,
        });
      } catch (err) {
        const errorValue = getErrorValue(params.value);

        updateErrorInContext({
          gridApi,
          rowId: params.rowId,
          colId,
          errorData: {
            message:
              err?.response?.data?.error?.message ??
              t('Something went wrong during update'),
            errorValue,
          },
        });
      }

      gridApi?.refreshCells();
    },
    [updateColumn, gridApi, t],
  );

  const setConfirmationModal = useSetConfirmationModal();

  const handleUpdateNameCell = useCallback(
    async (params: NewValueParams<MediaPlan.MediaData, string>) => {
      if (!params.colDef.field) {
        return;
      }
      const field = params.colDef.field;
      const rowId = params.data.id;
      const value = params.newRawValue;

      if (!value) {
        return;
      }

      await updateColumnWithErrorHandling({ rowId, field, value });
    },
    [updateColumnWithErrorHandling],
  );

  const handleCellUpdate = useCallback(
    async (params: NewValueParams<MediaPlan.MediaData, string | number>) => {
      if (!params.colDef.field || params.newRawValue === undefined) {
        return;
      }

      const field = getColName(params.colDef.field);
      const rowId = params.data.id;
      let value = params.newRawValue?.toString() ?? EMPTY_CELL_VALUE;

      if (value === '') {
        value = EMPTY_CELL_VALUE;
      }

      await updateColumnWithErrorHandling({ rowId, field, value });
    },
    [updateColumnWithErrorHandling],
  );

  const handleCellValueClear = useCallback(
    async (params: IMenuActionParams<MediaPlan.MediaData>) => {
      const field =
        params.column?.getColDef().field === 'name'
          ? 'name'
          : getColName(params.column?.getColDef().field ?? '');

      const rowId = params.node?.data?.id;
      const value = null;

      if (!field || !rowId) {
        return;
      }

      await updateColumnWithErrorHandling({ rowId, field, value });
    },
    [updateColumnWithErrorHandling],
  );

  const handleCurrencyUpdate = useCallback(
    (
      props: NewValueParams<MediaPlan.MediaData, string[] | string>,
      field: string,
    ) => {
      const mustConfirm =
        !ff.planningV2currencyWithoutConversion.enabled ||
        props.data.mediaRowProps?.isConfigured;

      if (mustConfirm) {
        setConfirmationModal({ ...props, updateColumnWithErrorHandling });
        return;
      }

      const value = Array.isArray(props.newRawValue)
        ? (props.newRawValue[0] ?? EMPTY_CELL_VALUE)
        : (props.newRawValue ?? EMPTY_CELL_VALUE);

      void updateColumnWithErrorHandling({
        rowId: props.data.id,
        field,
        value,
      });
    },
    [
      updateColumnWithErrorHandling,
      setConfirmationModal,
      ff.planningV2currencyWithoutConversion.enabled,
    ],
  );

  const handleSingleSelectUpdate = useCallback(
    async (props: NewValueParams<MediaPlan.MediaData, string[] | string>) => {
      const field = getColName(props.colDef.field ?? '');
      const rowId = props.data.id;
      const value = props.newRawValue ?? EMPTY_CELL_VALUE;

      if (field === SYSTEM_CURRENCY) {
        handleCurrencyUpdate(props, field);
        return;
      }

      await updateColumnWithErrorHandling({ rowId, field, value });
    },
    [updateColumnWithErrorHandling, handleCurrencyUpdate],
  );

  const handleGoalUpdate = useCallback(
    async (
      params: NewValueParams<MediaPlan.MediaData, GoalCellValue | null>,
    ) => {
      if (params.newRawValue === undefined) {
        return;
      }

      const field = getColName(params.colDef.field ?? '');
      const rowId = params.data.id;

      const value = params.newRawValue
        ? {
            refUrn: params.newRawValue.id,
            value: params.newRawValue.value?.toString() ?? null,
          }
        : null;

      await updateColumnWithErrorHandling({ rowId, field, value });
    },
    [updateColumnWithErrorHandling],
  );

  return useMemo(
    () =>
      ({
        name: handleUpdateNameCell,
        basic: handleCellUpdate,
        clear: handleCellValueClear,
        select: handleSingleSelectUpdate,
        reference: handleGoalUpdate,
      }) satisfies CellHandlersType,
    [
      handleUpdateNameCell,
      handleCellUpdate,
      handleCellValueClear,
      handleSingleSelectUpdate,
      handleGoalUpdate,
    ],
  );
};
